import { useEffect, useState, useCallback, useLayoutEffect, useRef } from "react";
import { Select, Pagination } from "antd";
import axios from "config/axios";
import constant from "constant/action";
import WorkContainer from "components/WorkContainer";
import { OperatingModeAttribute } from "types/user";
import { MachiningLineAttribute, MachiningLineSelectOption } from "types/machiningLine";
import { WORK_CONTAINER_SIZES } from "components/WorkContainer/constants";
import { BASE_W, BASE_H } from "./constants";
import "./style.scss";

interface Paginate {
  current_page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
}

const OperatingMode = () => {
  const [splitNumber, setSplitNumber] = useState<number>(6);
  const [paginate, setPaginate] = useState<Paginate>({ current_page: 1, per_page: 6, total_count: 0, total_pages: 0 });
  const [page, setPage] = useState(1);
  const [userList, setUserList] = useState<OperatingModeAttribute[]>([]);
  const [scale, setScale] = useState<number>(0.375);
  const gridItemRef = useRef<HTMLDivElement>(null);
  const [lineList, setLineList] = useState<MachiningLineSelectOption[]>([]);
  const [selectedLine, setSelectedLine] = useState<MachiningLineSelectOption | null>(null);

  const handleSelectSplitNumber = (value: number) => {
    setSplitNumber(value);
    setPage(1);
    setUserList([]); // 分割数変更時にリストをクリア
  };

  const handleSelectLine = (value: number, option: MachiningLineSelectOption | MachiningLineSelectOption[]) => {
    if (!value) {
      setSelectedLine(null);
    } else if (Array.isArray(option)) {
      setSelectedLine(option[0]);
    } else {
      setSelectedLine(option);
    }
    setPage(1);
    setUserList([]); // ライン変更時にリストをクリア
  };

  const splitOptions = [
    { value: 4, label: "4" },
    { value: 6, label: "6" },
  ];

  const fetch = useCallback(async () => {
    const url = constant.ADMIN_USERS_OPERATING_MODE;
    const params: any = {
      page: page,
      per_page: splitNumber,
    };
    if (selectedLine && selectedLine.value) {
      params.machining_line_id = selectedLine.value;
    }
    await axios.get(url, { params }).then((response) => {
      // レスポンスデータを常に上書きする（追加しない）
      const newData = response.data.data || [];
      setUserList(newData);
      setPaginate(response.data.meta);
    });
  }, [page, splitNumber, selectedLine]);

  useEffect(() => {
    const fetchMachiningLines = async () => {
      try {
        const url = constant.MACHINING_LINES;
        const response = await axios.get(url);
        if (response && response.data) {
          const lineOptions = response.data.data.map((item: MachiningLineAttribute) => {
            return { value: item.id, label: item.line_jpn };
          });
          setLineList(lineOptions);
        }
      } catch (error) {
        // Handle error
      }
    };
    fetchMachiningLines();
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetch();
    }, 10 * 1000);
    return () => clearInterval(interval);
  }, [fetch]);

  useLayoutEffect(() => {
    const calculateScale = () => {
      if (!gridItemRef.current) return;

      const gridItem = gridItemRef.current;
      const gridItemRect = gridItem.getBoundingClientRect();

      const appScale = Math.min(window.innerWidth / BASE_W, window.innerHeight / BASE_H);

      const gridItemWidth = gridItemRect.width / appScale;
      const gridItemHeight = gridItemRect.height / appScale;

      // 4分割と6分割で同じコンテナサイズを使用
      const workContainerOriginalWidth = WORK_CONTAINER_SIZES.LARGE.WIDTH;
      const workContainerOriginalHeight = WORK_CONTAINER_SIZES.LARGE.HEIGHT;

      const scaleX = gridItemWidth / workContainerOriginalWidth;
      const scaleY = gridItemHeight / workContainerOriginalHeight;
      const calculatedScale = Math.min(scaleX, scaleY);

      setScale(calculatedScale);
    };

    calculateScale();
    window.addEventListener("resize", calculateScale);
    return () => window.removeEventListener("resize", calculateScale);
  }, [splitNumber, userList]);

  const changePage = (page: number) => {
    setPage(page);
    setUserList([]); // ページ変更時にリストをクリア
  };

  const gridClass = splitNumber === 4 ? "split4" : "split6";

  // 表示するユーザーリストをsplitNumberで制限
  const displayUserList = userList.slice(0, splitNumber);

  return (
    <div className="operating-mode-container">
      <div className="selector-wrapper">
        <div className="split-number-selector">
          <label htmlFor="split-number" className="label">画面分割数</label>
          <Select
            id="split-number"
            value={splitNumber}
            options={splitOptions}
            onChange={handleSelectSplitNumber}
            style={{ width: '70px' }}
            className="select"
          />
        </div>
        <div className="split-number-selector" style={{ marginLeft: '20px' }}>
          <label htmlFor="line-filter" className="label">絞り込み</label>
          <Select
            id="line-filter"
            value={selectedLine?.value}
            options={lineList}
            onChange={handleSelectLine}
            className="select"
            style={{ width: '250px' }}
            placeholder="ライン"
            allowClear
          />
        </div>
      </div>
      <div className="split-content">
        <div className={`grid-container ${gridClass}`}>
          {displayUserList.map((user, index) => (
            <div
              className="grid-item"
              key={`record-${page}-${user.machining_line_id}-${user.machining_process_id}`}
              ref={index === 0 ? gridItemRef : null}
            >
              <div
                className="work-container-wrapper"
                style={{
                  width: `${WORK_CONTAINER_SIZES.LARGE.WIDTH}px`,
                  height: `${WORK_CONTAINER_SIZES.LARGE.HEIGHT}px`,
                }}
              >
                {splitNumber === 4 ? (
                  <WorkContainer
                    currentUserRecord={user}
                    scale={scale}
                    ringWidth={120}
                    avatarWidth={330}
                    avatarContainerWidth={275}
                    avatarMaxWidth={330}
                    avatarTop={48}
                    avatarRight={-30}
                    avatarTransformY={0}
                    adjustLegendPosition={1.6}
                    showUserName={true}
                  />
                ) : (
                  <WorkContainer
                    currentUserRecord={user}
                    scale={scale}
                    ringWidth={130}
                    avatarWidth={315}
                    avatarContainerWidth={250}
                    avatarMaxWidth={315}
                    avatarTop={48}
                    avatarRight={-25}
                    avatarTransformY={0}
                    adjustLegendPosition={1.7}
                    showUserName={true}
                  />
                )}
              </div>
            </div>
          ))}
          {Array.from({ length: Math.max(0, splitNumber - displayUserList.length) }).map((_, index) => (
            <div
              className="grid-item empty"
              key={`empty-${page}-${index}`}
              ref={displayUserList.length === 0 && index === 0 ? gridItemRef : null}
            >
              <div
                className="work-container-wrapper"
                style={{
                  width: `${WORK_CONTAINER_SIZES.LARGE.WIDTH}px`,
                  height: `${WORK_CONTAINER_SIZES.LARGE.HEIGHT}px`,
                }}
              >
              </div>
            </div>
          ))}
        </div>
        <div className="pagination-wrapper">
          <Pagination
            current={paginate.current_page}
            pageSize={paginate.per_page}
            total={paginate.total_count}
            showLessItems={true}
            onChange={changePage}
          />
        </div>
      </div>
    </div>
  );
};

export default OperatingMode;
